import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Container, Typography, IconButton, Button, Divider } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import {
    ArrowBackOutlined, WifiOutlined, CheckCircleOutline,
    LockOutlined, NorthEastOutlined,
} from '@mui/icons-material';

const B = {
    deep:    '#0D0820',
    dark:    '#1A0F3D',
    mid:     '#2D1B69',
    purple:  '#7B5EA7',
    light:   '#9B7EC8',
    lavender:'#F7F4FC',
    ink:     '#0D0A1A',
    green:   '#00C896',
    greenDim:'#00C89618',
    red:     '#FF4D6A',
    redDim:  '#FF4D6A18',
    blue:    '#007AFF',
};

const NETWORKS = [
    { value: 'mtn',     label: 'MTN',     color: '#FFCC00', bg: '#FFCC0018', logo: '/images/mtn-new-logo.svg'  },
    { value: 'glo',     label: 'Glo',     color: '#00A650', bg: '#00A65018', logo: '/images/glo.jpg' },
    { value: 'airtel',  label: 'Airtel',  color: '#FF0000', bg: '#FF000018', logo: '/images/airtel-logo.png' },
    { value: '9mobile', label: '9mobile', color: '#00A859', bg: '#00A85918', logo: '/images/9mobile.jpg' },
];

interface DataPlan {
    value: string; label: string; data: string; validity: string; price: number; popular?: boolean;
}

const DATA_PLANS: Record<string, DataPlan[]> = {
    mtn: [
        { value: 'mtn-300mb', label: '300 MB',  data: '300MB',  validity: '7 Days',  price: 150  },
        { value: 'mtn-1gb',   label: '1 GB',    data: '1GB',    validity: '30 Days', price: 500,  popular: true },
        { value: 'mtn-2gb',   label: '2 GB',    data: '2GB',    validity: '30 Days', price: 1000 },
        { value: 'mtn-5gb',   label: '5 GB',    data: '5GB',    validity: '30 Days', price: 1500 },
        { value: 'mtn-10gb',  label: '10 GB',   data: '10GB',   validity: '30 Days', price: 2500 },
        { value: 'mtn-20gb',  label: '20 GB',   data: '20GB',   validity: '30 Days', price: 3500 },
    ],
    glo: [
        { value: 'glo-500mb', label: '500 MB',  data: '500MB',  validity: '14 Days', price: 200  },
        { value: 'glo-1gb',   label: '1 GB',    data: '1GB',    validity: '14 Days', price: 400,  popular: true },
        { value: 'glo-2gb',   label: '2 GB',    data: '2GB',    validity: '30 Days', price: 800  },
        { value: 'glo-5gb',   label: '5 GB',    data: '5GB',    validity: '30 Days', price: 1500 },
        { value: 'glo-10gb',  label: '10 GB',   data: '10GB',   validity: '30 Days', price: 2000 },
    ],
    airtel: [
        { value: 'airtel-500mb',  label: '500 MB',  data: '500MB',   validity: '7 Days',  price: 300  },
        { value: 'airtel-1.5gb', label: '1.5 GB',  data: '1.5GB',   validity: '30 Days', price: 1000, popular: true },
        { value: 'airtel-4.5gb', label: '4.5 GB',  data: '4.5GB',   validity: '30 Days', price: 2000 },
        { value: 'airtel-10gb',  label: '10 GB',   data: '10GB',    validity: '30 Days', price: 3000 },
    ],
    '9mobile': [
        { value: '9m-1gb',   label: '1 GB',   data: '1GB',   validity: '30 Days', price: 700,  popular: true },
        { value: '9m-2.5gb', label: '2.5 GB', data: '2.5GB', validity: '30 Days', price: 1500 },
        { value: '9m-5gb',   label: '5 GB',   data: '5GB',   validity: '30 Days', price: 2000 },
    ],
};

const RECENT_TXS = [
    { id: 1, title: 'MTN Data Bundle',    sub: '080 *** 4521 · 1GB/30d', amount: 500,  date: 'Today · 8:02 AM'      },
    { id: 2, title: 'Airtel Data Bundle', sub: '081 *** 3310 · 1.5GB',   amount: 1000, date: 'Yesterday · 3:15 PM'  },
    { id: 3, title: 'Glo Data Bundle',    sub: '081 *** 7722 · 2GB',     amount: 800,  date: 'Mon · 10:45 AM'       },
    { id: 4, title: 'MTN Data Bundle',    sub: '080 *** 4521 · 5GB',     amount: 1500, date: 'Sun · 6:30 PM'        },
];

const fmt = (n: number) =>
    new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(n);

const DataPage: React.FC = () => {
    const navigate = useNavigate();
    const theme    = useTheme();
    const isDark   = theme.palette.mode === 'dark';

    const [step,     setStep]     = useState<1 | 2>(1);
    const [network,  setNetwork]  = useState('');
    const [phone,    setPhone]    = useState('');
    const [phoneTouched, setPhoneTouched] = useState(false);
    const [plan,     setPlan]     = useState('');
    const [loading,  setLoading]  = useState(false);
    const [done,     setDone]     = useState(false);

    const balance     = 125000;
    const selectedNet = NETWORKS.find(n => n.value === network);
    const plans       = DATA_PLANS[network] || [];
    const selectedPlan = plans.find(p => p.value === plan);

    const NG_PHONE_RE = /^(?:0[7-9][0-1]\d{8}|[7-9][0-1]\d{8})$/;
    const phoneClean  = phone.replace(/\s+/g, '');
    const phoneValid  = NG_PHONE_RE.test(phoneClean);
    const phoneError  = phoneTouched && phone.length > 0 && !phoneValid
        ? phoneClean.length < 10
            ? 'Number too short — must be 11 digits'
            : /[^0-9]/.test(phoneClean)
                ? 'Numbers only, no letters or symbols'
                : 'Enter a valid Nigerian number (e.g. 080 1234 5678)'
        : '';

    const pageBg     = isDark ? B.deep : B.lavender;
    const surfaceBg  = isDark ? alpha(B.mid, .55) : '#fff';
    const surfaceBdr = `1px solid ${alpha(isDark ? '#fff' : B.dark, .07)}`;
    const surfaceShd = `0 4px 28px ${alpha(B.dark, isDark ? .25 : .06)}`;
    const textPri    = isDark ? '#fff' : B.ink;
    const textMid    = alpha(isDark ? '#fff' : B.ink, .45);
    const textFaint  = alpha(isDark ? '#fff' : B.ink, .28);
    const dividerC   = alpha(isDark ? '#fff' : B.dark, .08);

    const glassCard = {
        background: surfaceBg, border: surfaceBdr, boxShadow: surfaceShd,
        borderRadius: '20px', backdropFilter: isDark ? 'blur(20px)' : 'none',
    };

    const handleConfirm = () => {
        setLoading(true);
        setTimeout(() => { setLoading(false); setDone(true); }, 1800);
    };

    const goBack = () => {
        if (step === 1) navigate(-1);
        else setStep(1);
    };

    /* ── SUCCESS ── */
    if (done) return (
        <Box sx={{ minHeight: '100vh', background: isDark ? `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)` : B.lavender, display: 'flex', alignItems: 'center', justifyContent: 'center', px: 3 }}>
            <Box sx={{ textAlign: 'center' }}>
                <Box sx={{ width: 80, height: 80, borderRadius: '50%', background: B.greenDim, border: `2px solid ${B.green}`, display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 3 }}>
                    <CheckCircleOutline sx={{ color: B.green, fontSize: 40 }} />
                </Box>
                <Typography sx={{ color: textPri, fontFamily: "'Instrument Serif', serif", fontSize: '1.9rem', mb: 1 }}>Data Activated!</Typography>
                <Typography sx={{ color: textMid, fontSize: '.85rem', mb: .5 }}>{selectedPlan?.data} sent to</Typography>
                <Typography sx={{ color: textPri, fontWeight: 700, fontSize: '1rem', mb: .25 }}>{phone}</Typography>
                <Typography sx={{ color: textFaint, fontSize: '.78rem', mb: 1 }}>via {selectedNet?.label} · Valid for {selectedPlan?.validity}</Typography>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: .75, background: alpha(B.green, .1), border: `1px solid ${alpha(B.green, .2)}`, borderRadius: 20, px: 2, py: .6, mb: 4 }}>
                    <Typography sx={{ color: B.green, fontSize: '.72rem', fontWeight: 700 }}>New Balance: {fmt(balance - (selectedPlan?.price ?? 0))}</Typography>
                </Box>
                <Box display="flex" gap={2} justifyContent="center">
                    <Button onClick={() => { setDone(false); setStep(1); setPhone(''); setNetwork(''); setPlan(''); }}
                            sx={{ border: `1px solid ${alpha(isDark ? '#fff' : B.dark, .12)}`, color: textMid, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 600, fontSize: '.85rem' }}>
                        Buy Again
                    </Button>
                    <Button onClick={() => navigate('/wallet')} variant="contained"
                            sx={{ background: `linear-gradient(135deg, ${B.purple}, ${B.light})`, borderRadius: 50, px: 3.5, py: 1.2, textTransform: 'none', fontWeight: 700, fontSize: '.85rem', boxShadow: `0 12px 32px ${alpha(B.purple, .4)}` }}>
                        Done
                    </Button>
                </Box>
            </Box>
        </Box>
    );

    return (
        <Box sx={{ minHeight: '100vh', background: pageBg, pb: 10, fontFamily: "'DM Sans', system-ui, sans-serif" }}>

            {/* ══ HERO ══ */}
            <Box sx={{
                background: `linear-gradient(160deg, ${B.deep} 0%, ${B.mid} 55%, ${B.purple} 100%)`,
                mt: { xs: '-76px', sm: 0 },
                pt: { xs: '80px', sm: '28px' },
                pb: { xs: 4, sm: 14 }, px: 3, position: 'relative', overflow: 'hidden',
            }}>
                <Box sx={{ position: 'absolute', top: -70, right: -50, width: 240, height: 240, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.light, .18)} 0%, transparent 70%)`, pointerEvents: 'none' }} />
                <Box sx={{ position: 'absolute', bottom: -10, left: -40, width: 200, height: 200, borderRadius: '50%', background: `radial-gradient(circle, ${alpha(B.blue, .08)} 0%, transparent 70%)`, pointerEvents: 'none' }} />

                <Box display="flex" alignItems="center" gap={1.5}>
                    <IconButton onClick={goBack}
                                sx={{ color: alpha('#fff', .7), background: alpha('#fff', .1), border: `1px solid ${alpha('#fff', .15)}`, width: 36, height: 36, '&:hover': { background: alpha('#fff', .2) } }}>
                        <ArrowBackOutlined sx={{ fontSize: 17 }} />
                    </IconButton>
                    <Box>
                        <Typography sx={{ color: alpha('#fff', .35), fontSize: '.62rem', letterSpacing: '.18em', textTransform: 'uppercase' }}>
                            Step {step} of 2
                        </Typography>
                        <Typography sx={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem', letterSpacing: '-.025em', fontFamily: "'Instrument Serif', serif" }}>
                            Buy Data
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto', background: alpha(B.green, .15), border: `1px solid ${alpha(B.green, .3)}`, borderRadius: 20, px: 1.5, py: .5 }}>
                        <Typography sx={{ color: B.green, fontSize: '.68rem', fontWeight: 700 }}>Bal: {fmt(balance)}</Typography>
                    </Box>
                </Box>
            </Box>

            <Container maxWidth="sm" sx={{ mt: { xs: 2, sm: -8 }, position: 'relative', zIndex: 1, px: { xs: 2, sm: 3 } }}>

                {/* ── STEP 1 ── */}
                {step === 1 && (
                    <>
                        {/* Network */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Select Network</Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
                                {NETWORKS.map(n => (
                                    <Box key={n.value} onClick={() => { setNetwork(n.value); setPlan(''); }} sx={{
                                        borderRadius: '14px', p: 1.5, cursor: 'pointer', textAlign: 'center',
                                        background: network === n.value ? alpha(n.color, .12) : alpha(isDark ? '#fff' : B.dark, .03),
                                        border: `1.5px solid ${network === n.value ? alpha(n.color, .4) : dividerC}`,
                                        transition: 'all .18s',
                                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: .75,
                                    }}>
                                        <Box sx={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <img src={n.logo} alt={n.label}
                                                 style={{ width: 36, height: 36, objectFit: 'contain' }}
                                                 onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }}
                                            />
                                        </Box>
                                        <Typography sx={{ color: network === n.value ? n.color : textMid, fontSize: '.7rem', fontWeight: 700 }}>{n.label}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>

                        {/* Phone number */}
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2, border: phoneTouched && phoneError ? `1.5px solid ${alpha(B.red, .4)}` : (phoneTouched && phoneValid ? `1.5px solid ${alpha(B.green, .4)}` : surfaceBdr) }}>
                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 1.5, fontWeight: 600 }}>Phone Number</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Box sx={{ background: alpha(isDark ? '#fff' : B.dark, .05), border: `1px solid ${dividerC}`, borderRadius: '12px', px: 1.5, py: 1.25, flexShrink: 0 }}>
                                    <Typography sx={{ color: textMid, fontSize: '.82rem', fontWeight: 600 }}>+234</Typography>
                                </Box>
                                <input
                                    type="tel" placeholder="080 0000 0000"
                                    value={phone}
                                    onChange={e => {
                                        const val = e.target.value.replace(/[^0-9\s]/g, '');
                                        setPhone(val);
                                    }}
                                    onBlur={() => setPhoneTouched(true)}
                                    style={{
                                        flex: 1, background: 'transparent', border: 'none', outline: 'none',
                                        color: textPri, fontSize: '1rem', fontWeight: 600,
                                        fontFamily: "'DM Sans', system-ui, sans-serif",
                                    }}
                                />
                                {phoneTouched && phone.length > 0 && (
                                    <Typography sx={{ fontSize: '1rem', flexShrink: 0 }}>
                                        {phoneValid ? '✅' : '❌'}
                                    </Typography>
                                )}
                            </Box>
                            <Box sx={{ mt: 1.5, height: '1px', background: `linear-gradient(90deg, ${phoneTouched && phoneError ? B.red : phoneTouched && phoneValid ? B.green : B.blue}, transparent)` }} />
                            {phoneError && (
                                <Typography sx={{ color: B.red, fontSize: '.7rem', mt: 1, fontWeight: 500 }}>
                                    {phoneError}
                                </Typography>
                            )}
                            {phoneTouched && phoneValid && (
                                <Typography sx={{ color: B.green, fontSize: '.7rem', mt: 1, fontWeight: 500 }}>
                                    Valid Nigerian number ✓
                                </Typography>
                            )}
                        </Box>

                        {/* Data Plans */}
                        {network && (
                            <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                                <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Select Plan</Typography>
                                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1.5 }}>
                                    {plans.map(p => {
                                        const isSelected = plan === p.value;
                                        return (
                                            <Box key={p.value} onClick={() => setPlan(p.value)} sx={{
                                                borderRadius: '14px', p: 2, cursor: 'pointer', position: 'relative',
                                                background: isSelected ? alpha(B.blue, .1) : alpha(isDark ? '#fff' : B.dark, .03),
                                                border: `1.5px solid ${isSelected ? alpha(B.blue, .4) : dividerC}`,
                                                transition: 'all .18s',
                                            }}>
                                                {p.popular && (
                                                    <Box sx={{ position: 'absolute', top: -1, right: 10, background: `linear-gradient(135deg, ${B.blue}, #5AC8FA)`, borderRadius: '0 0 8px 8px', px: 1, py: .25 }}>
                                                        <Typography sx={{ color: '#fff', fontSize: '.55rem', fontWeight: 700, letterSpacing: '.05em' }}>POPULAR</Typography>
                                                    </Box>
                                                )}
                                                <Typography sx={{ color: isSelected ? B.blue : textPri, fontWeight: 800, fontSize: '1.1rem', fontFamily: "'Instrument Serif', serif" }}>{p.data}</Typography>
                                                <Typography sx={{ color: textFaint, fontSize: '.68rem', mb: 1 }}>{p.validity}</Typography>
                                                <Typography sx={{ color: isSelected ? B.blue : textPri, fontWeight: 700, fontSize: '.85rem' }}>₦{p.price.toLocaleString()}</Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        )}

                        <Button fullWidth disabled={!network || !phoneValid || !plan} onClick={() => setStep(2)}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.blue}, #5AC8FA)`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.blue, .35)}`,
                                    '&:disabled': { opacity: .35, boxShadow: 'none' },
                                    '&:hover': { boxShadow: `0 16px 40px ${alpha(B.blue, .5)}` },
                                }}>
                            Continue
                        </Button>
                    </>
                )}

                {/* ── STEP 2: Confirm ── */}
                {step === 2 && selectedPlan && (
                    <>
                        <Box sx={{ ...glassCard, p: 2.5, mb: 2 }}>
                            {/* Plan highlight */}
                            <Box sx={{ borderRadius: '14px', p: 2.5, mb: 2.5, background: alpha(B.blue, .08), border: `1px solid ${alpha(B.blue, .2)}`, textAlign: 'center' }}>
                                <Typography sx={{ color: B.blue, fontFamily: "'Instrument Serif', serif", fontSize: '2.2rem', fontWeight: 700 }}>{selectedPlan.data}</Typography>
                                <Typography sx={{ color: textMid, fontSize: '.78rem' }}>{selectedPlan.validity} · {selectedNet?.label}</Typography>
                            </Box>

                            <Typography sx={{ color: textFaint, fontSize: '.6rem', letterSpacing: '.15em', textTransform: 'uppercase', mb: 2, fontWeight: 600 }}>Confirm Details</Typography>

                            {[
                                { label: 'Phone Number', value: phone },
                                { label: 'Network',      value: selectedNet?.label ?? '' },
                                { label: 'Plan',         value: selectedPlan.label },
                                { label: 'Validity',     value: selectedPlan.validity },
                                { label: 'Amount',       value: fmt(selectedPlan.price) },
                                { label: 'Fee',          value: 'Free' },
                            ].map((row, i, arr) => (
                                <React.Fragment key={row.label}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" py={1.1}>
                                        <Typography sx={{ color: textFaint, fontSize: '.8rem' }}>{row.label}</Typography>
                                        <Typography sx={{ color: row.label === 'Amount' ? B.blue : textPri, fontWeight: row.label === 'Amount' ? 700 : 600, fontSize: '.85rem' }}>{row.value}</Typography>
                                    </Box>
                                    {i < arr.length - 1 && <Divider sx={{ borderColor: dividerC }} />}
                                </React.Fragment>
                            ))}

                            <Box sx={{ mt: 2, p: 1.5, borderRadius: '12px', background: alpha(B.blue, .08), border: `1px solid ${alpha(B.blue, .2)}` }}>
                                <Typography sx={{ color: B.blue, fontSize: '.75rem', fontWeight: 600, textAlign: 'center' }}>
                                    {fmt(selectedPlan.price)} will be deducted from your wallet
                                </Typography>
                            </Box>
                        </Box>

                        <Button fullWidth onClick={handleConfirm} disabled={loading}
                                sx={{
                                    py: 1.65, borderRadius: '14px', textTransform: 'none', fontWeight: 700, fontSize: '.95rem',
                                    background: `linear-gradient(135deg, ${B.blue}, #5AC8FA)`,
                                    color: '#fff', boxShadow: `0 12px 32px ${alpha(B.blue, .35)}`,
                                    '&:disabled': { opacity: .5, boxShadow: 'none' },
                                }}>
                            {loading ? 'Activating…' : `Buy ${selectedPlan.data} for ${fmt(selectedPlan.price)}`}
                        </Button>
                    </>
                )}

                {/* ── Recent Transactions ── */}
                <Box sx={{ ...glassCard, mt: 3, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, pt: 2.5, pb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '.95rem', letterSpacing: '-.02em', color: textPri }}>Recent Data</Typography>
                        <Button size="small" onClick={() => navigate('/history')}
                                sx={{ color: B.purple, fontWeight: 600, fontSize: '.7rem', textTransform: 'none', px: 1, borderRadius: 2, '&:hover': { background: alpha(B.purple, .07) } }}>
                            See all
                        </Button>
                    </Box>

                    {RECENT_TXS.map((tx, i) => (
                        <React.Fragment key={tx.id}>
                            <Box sx={{ px: 2.5, py: 1.75, display: 'flex', alignItems: 'center', gap: 2, cursor: 'pointer', transition: 'background .15s', '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .025) } }}>
                                <Box sx={{ width: 40, height: 40, borderRadius: 2.5, flexShrink: 0, background: alpha(B.blue, .12), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <WifiOutlined sx={{ color: B.blue, fontSize: 16 }} />
                                </Box>
                                <Box flex={1} minWidth={0}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '.84rem', color: textPri, letterSpacing: '-.015em' }}>{tx.title}</Typography>
                                    <Typography sx={{ fontSize: '.68rem', color: textFaint, mt: .15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{tx.sub} · {tx.date}</Typography>
                                </Box>
                                <Typography sx={{ fontWeight: 700, fontSize: '.86rem', color: textPri, flexShrink: 0, fontVariantNumeric: 'tabular-nums' }}>
                                    −₦{tx.amount.toLocaleString()}
                                </Typography>
                            </Box>
                            {i < RECENT_TXS.length - 1 && <Divider sx={{ mx: 2.5, borderColor: alpha(isDark ? '#fff' : B.dark, .055) }} />}
                        </React.Fragment>
                    ))}

                    <Box sx={{ px: 2.5, py: 2, borderTop: `1px solid ${alpha(isDark ? '#fff' : B.dark, .055)}` }}>
                        <Button fullWidth onClick={() => navigate('/history')}
                                sx={{ color: textFaint, textTransform: 'none', fontWeight: 600, fontSize: '.76rem', py: .75, borderRadius: 2, '&:hover': { background: alpha(isDark ? '#fff' : B.dark, .035), color: textPri } }}>
                            View full history
                        </Button>
                    </Box>
                </Box>

                {/* footer */}
                <Box sx={{ mt: 3.5, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: .75 }}>
                    <LockOutlined sx={{ fontSize: 11, color: alpha(isDark ? '#fff' : B.ink, .18) }} />
                    <Typography sx={{ fontSize: '.62rem', color: alpha(isDark ? '#fff' : B.ink, .18), letterSpacing: '.05em' }}>
                        256-BIT ENCRYPTION · NG CREDIT FINANCIAL SERVICES
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default DataPage;